'use client'

import {
    PermissionsMap,
    Role,
    RolePermissions,
} from '@/lib/api/common/interfaces/role.interface'
import {
    createRole,
    getPermissionsMap,
    getRole,
    getRoles,
    updateRole,
} from '@/lib/api/roles/roles-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { moduleLabel, resourceLabel } from '@/layouts/admin/segment-labels'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import {
    MdArrowBack,
    MdCheckBox,
    MdCheckBoxOutlineBlank,
    MdContentCopy,
    MdDescription,
    MdExpandLess,
    MdExpandMore,
    MdIndeterminateCheckBox,
    MdLock,
    MdShield,
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

const ACTION_LABELS: Record<string, string> = {
    read: 'Visualizar',
    create: 'Criar',
    update: 'Editar',
    delete: 'Excluir',
}

const ACTIONS = ['read', 'create', 'update', 'delete']

export default function RoleFormPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id
    // O RouteAccessGuard só exige `read` para abrir a tela; sem isto, quem tem apenas
    // leitura preenche o formulário inteiro e leva 403 do backend ao salvar.
    const { canCreate, canUpdate } = useResourcePermissions(
        'security',
        'permissions'
    )
    const canSave = isEditing ? canUpdate : canCreate
    // Os labels do mapa de permissões vêm do backend na linguagem da saúde
    // ("Atendimento Clínico", "Consultas"). A tradução por segmento é a mesma do
    // menu (`segment-labels.ts`), para o perfil mostrar os nomes que o tenant vê.
    const segment = user?.company?.segment || 'OTHER'
    const secondarySegment = user?.company?.secondarySegment || 'OTHER'
    const getResourceLabel = (
        moduleKey: string,
        resourceKey: string,
        fallback: string
    ) =>
        segment === 'HEALTH' &&
        secondarySegment !== 'INTERMEDIATION' &&
        resourceKey === 'clients'
            ? 'Pacientes'
            : resourceLabel(segment, moduleKey, resourceKey, fallback)
    const [loading, setLoading] = useState(false)
    const [loadingRole, setLoadingRole] = useState(false)
    const [permissionsMap, setPermissionsMap] = useState<PermissionsMap>({})
    const [loadingMap, setLoadingMap] = useState(true)
    const [expandedModules, setExpandedModules] = useState<
        Record<string, boolean>
    >({})

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        defaultForType: '' as string,
    })

    const [permissions, setPermissions] = useState<
        Record<string, Record<string, string[]>>
    >({})
    const [rawRolePermissions, setRawRolePermissions] =
        useState<RolePermissions | null>(null)

    const [roles, setRoles] = useState<Role[]>([])
    const [copyFromId, setCopyFromId] = useState('')

    useEffect(() => {
        loadPermissionsMap()
        loadRoles()
    }, [])

    useEffect(() => {
        if (isEditing && id) {
            loadRole()
        }
    }, [id, isEditing])

    useEffect(() => {
        if (
            rawRolePermissions &&
            Object.keys(permissionsMap).length > 0
        ) {
            parsePermissions(rawRolePermissions)
        }
    }, [rawRolePermissions, permissionsMap])

    const loadPermissionsMap = async () => {
        setLoadingMap(true)
        try {
            const response = await getPermissionsMap()
            if (response.success) {
                setPermissionsMap(response.result as PermissionsMap)
            } else {
                toast.error('Erro ao carregar mapa de permissões')
            }
        } catch (error) {
            toast.error('Erro ao carregar mapa de permissões')
        } finally {
            setLoadingMap(false)
        }
    }

    const loadRoles = async () => {
        try {
            const response = await getRoles()
            if (response.success && Array.isArray(response.result)) {
                setRoles(response.result)
            }
        } catch {
            // lista de cópia é opcional; falha silenciosa não bloqueia o form
        }
    }

    const handleCopyFrom = () => {
        if (!copyFromId) return
        const source = roles.find(r => String(r.id) === copyFromId)
        if (!source) return
        parsePermissions(source.permissions as RolePermissions)
        toast.success(`Permissões copiadas de "${source.name}"`)
    }

    const loadRole = async () => {
        if (!id) return

        setLoadingRole(true)
        try {
            const response = await getRole(parseInt(id, 10))
            if (response.success) {
                const role = Array.isArray(response.result)
                    ? response.result[0]
                    : response.result
                if (role) {
                    setFormData({
                        name: role.name || '',
                        description: role.description || '',
                        defaultForType: role.defaultForType || '',
                    })
                    setRawRolePermissions(role.permissions as RolePermissions)
                } else {
                    toast.error('Perfil de acesso não encontrado')
                    navigate('/security/permissions')
                }
            } else {
                toast.error(
                    response.error || 'Erro ao carregar perfil de acesso'
                )
                navigate('/security/permissions')
            }
        } catch (error) {
            toast.error('Erro ao carregar perfil de acesso')
            navigate('/security/permissions')
        } finally {
            setLoadingRole(false)
        }
    }

    const parsePermissions = (rolePerms: RolePermissions) => {
        if (!rolePerms || typeof rolePerms !== 'object') return

        const parsed: Record<string, Record<string, string[]>> = {}

        for (const [moduleKey, moduleValue] of Object.entries(rolePerms)) {
            if (moduleKey === '*') continue
            if (moduleValue === '*') {
                parsed[moduleKey] = {}
                const moduleMap = permissionsMap[moduleKey]
                if (moduleMap) {
                    for (const resourceKey of Object.keys(
                        moduleMap.resources
                    )) {
                        parsed[moduleKey][resourceKey] = [...ACTIONS]
                    }
                }
            } else if (typeof moduleValue === 'object') {
                parsed[moduleKey] = {}
                for (const [resourceKey, resourceValue] of Object.entries(
                    moduleValue
                )) {
                    if (resourceValue === '*') {
                        parsed[moduleKey][resourceKey] = [...ACTIONS]
                    } else if (Array.isArray(resourceValue)) {
                        parsed[moduleKey][resourceKey] = [...resourceValue]
                    }
                }
            }
        }

        setPermissions(parsed)
    }

    const toggleModule = (moduleKey: string) => {
        setExpandedModules(prev => ({
            ...prev,
            [moduleKey]: !prev[moduleKey],
        }))
    }

    const isActionChecked = (
        moduleKey: string,
        resourceKey: string,
        action: string
    ): boolean => {
        return (
            permissions[moduleKey]?.[resourceKey]?.includes(action) ?? false
        )
    }

    const toggleAction = (
        moduleKey: string,
        resourceKey: string,
        action: string
    ) => {
        setPermissions(prev => {
            const newPerms = { ...prev }
            if (!newPerms[moduleKey]) newPerms[moduleKey] = {}
            if (!newPerms[moduleKey][resourceKey])
                newPerms[moduleKey][resourceKey] = []

            const currentActions = [...newPerms[moduleKey][resourceKey]]
            const index = currentActions.indexOf(action)
            if (index >= 0) {
                currentActions.splice(index, 1)
            } else {
                currentActions.push(action)
            }

            if (currentActions.length === 0) {
                const { [resourceKey]: _, ...rest } = newPerms[moduleKey]
                newPerms[moduleKey] = rest
                if (Object.keys(newPerms[moduleKey]).length === 0) {
                    const { [moduleKey]: __, ...restModules } = newPerms
                    return restModules
                }
            } else {
                newPerms[moduleKey] = {
                    ...newPerms[moduleKey],
                    [resourceKey]: currentActions,
                }
            }

            return newPerms
        })
    }

    const getModuleState = (
        moduleKey: string
    ): 'none' | 'partial' | 'all' => {
        const moduleMap = permissionsMap[moduleKey]
        if (!moduleMap) return 'none'

        const resourceKeys = Object.keys(moduleMap.resources)
        let totalPossible = resourceKeys.length * ACTIONS.length
        let totalChecked = 0

        for (const resourceKey of resourceKeys) {
            for (const action of ACTIONS) {
                if (isActionChecked(moduleKey, resourceKey, action)) {
                    totalChecked++
                }
            }
        }

        if (totalChecked === 0) return 'none'
        if (totalChecked === totalPossible) return 'all'
        return 'partial'
    }

    const toggleAllModule = (moduleKey: string) => {
        const state = getModuleState(moduleKey)
        const moduleMap = permissionsMap[moduleKey]
        if (!moduleMap) return

        setPermissions(prev => {
            const newPerms = { ...prev }

            if (state === 'all') {
                const { [moduleKey]: _, ...rest } = newPerms
                return rest
            } else {
                newPerms[moduleKey] = {}
                for (const resourceKey of Object.keys(moduleMap.resources)) {
                    newPerms[moduleKey][resourceKey] = [...ACTIONS]
                }
                return newPerms
            }
        })
    }

    const getResourceState = (
        moduleKey: string,
        resourceKey: string
    ): 'none' | 'partial' | 'all' => {
        let totalChecked = 0
        for (const action of ACTIONS) {
            if (isActionChecked(moduleKey, resourceKey, action)) {
                totalChecked++
            }
        }
        if (totalChecked === 0) return 'none'
        if (totalChecked === ACTIONS.length) return 'all'
        return 'partial'
    }

    const toggleAllResource = (moduleKey: string, resourceKey: string) => {
        const state = getResourceState(moduleKey, resourceKey)

        setPermissions(prev => {
            const newPerms = { ...prev }
            if (!newPerms[moduleKey]) newPerms[moduleKey] = {}

            if (state === 'all') {
                const { [resourceKey]: _, ...rest } = newPerms[moduleKey]
                newPerms[moduleKey] = rest
                if (Object.keys(newPerms[moduleKey]).length === 0) {
                    const { [moduleKey]: __, ...restModules } = newPerms
                    return restModules
                }
            } else {
                newPerms[moduleKey] = {
                    ...newPerms[moduleKey],
                    [resourceKey]: [...ACTIONS],
                }
            }

            return newPerms
        })
    }

    const getColumnState = (
        moduleKey: string,
        action: string
    ): 'none' | 'partial' | 'all' => {
        const moduleMap = permissionsMap[moduleKey]
        if (!moduleMap) return 'none'

        const resourceKeys = Object.keys(moduleMap.resources)
        let totalChecked = 0

        for (const resourceKey of resourceKeys) {
            if (isActionChecked(moduleKey, resourceKey, action)) {
                totalChecked++
            }
        }

        if (totalChecked === 0) return 'none'
        if (totalChecked === resourceKeys.length) return 'all'
        return 'partial'
    }

    const toggleColumn = (moduleKey: string, action: string) => {
        const state = getColumnState(moduleKey, action)
        const moduleMap = permissionsMap[moduleKey]
        if (!moduleMap) return

        setPermissions(prev => {
            const newPerms = { ...prev }
            if (!newPerms[moduleKey]) newPerms[moduleKey] = {}

            for (const resourceKey of Object.keys(moduleMap.resources)) {
                if (!newPerms[moduleKey][resourceKey])
                    newPerms[moduleKey][resourceKey] = []

                const currentActions = [
                    ...newPerms[moduleKey][resourceKey],
                ]
                const index = currentActions.indexOf(action)

                if (state === 'all') {
                    if (index >= 0) currentActions.splice(index, 1)
                } else {
                    if (index < 0) currentActions.push(action)
                }

                if (currentActions.length === 0) {
                    const { [resourceKey]: _, ...rest } = newPerms[moduleKey]
                    newPerms[moduleKey] = rest
                } else {
                    newPerms[moduleKey] = {
                        ...newPerms[moduleKey],
                        [resourceKey]: currentActions,
                    }
                }
            }

            if (
                newPerms[moduleKey] &&
                Object.keys(newPerms[moduleKey]).length === 0
            ) {
                const { [moduleKey]: _, ...rest } = newPerms
                return rest
            }

            return newPerms
        })
    }

    const buildPermissionsPayload = (): Record<string, any> => {
        const result: Record<string, any> = {}
        for (const [moduleKey, resources] of Object.entries(permissions)) {
            result[moduleKey] = {}
            for (const [resourceKey, actions] of Object.entries(resources)) {
                if (actions.length > 0) {
                    result[moduleKey][resourceKey] = actions
                }
            }
        }
        return result
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!user?.companyId) {
            toast.error('Usuário não autenticado')
            return
        }

        if (!formData.name.trim()) {
            toast.warning('Preencha o nome do perfil')
            return
        }

        const permissionsPayload = buildPermissionsPayload()
        if (Object.keys(permissionsPayload).length === 0) {
            toast.warning('Selecione ao menos uma permissão')
            return
        }

        setLoading(true)

        try {
            let response
            const data = {
                name: formData.name.trim(),
                description: formData.description.trim() || undefined,
                permissions: permissionsPayload,
                defaultForType: formData.defaultForType || null,
            }

            if (isEditing) {
                response = await updateRole(parseInt(id!, 10), data)
            } else {
                response = await createRole(data)
            }

            if (response.success) {
                toast.success(
                    isEditing
                        ? 'Perfil de acesso atualizado com sucesso'
                        : 'Perfil de acesso criado com sucesso'
                )
                navigate('/security/permissions')
            } else {
                toast.error(
                    response.error ||
                    (isEditing
                        ? 'Erro ao atualizar perfil'
                        : 'Erro ao criar perfil')
                )
            }
        } catch (error) {
            toast.error(
                isEditing
                    ? 'Erro ao atualizar perfil'
                    : 'Erro ao criar perfil'
            )
        } finally {
            setLoading(false)
        }
    }

    const CheckboxIcon = ({
        state,
    }: {
        state: 'none' | 'partial' | 'all'
    }) => {
        if (state === 'all')
            return <MdCheckBox className="text-xl text-secondary" />
        if (state === 'partial')
            return (
                <MdIndeterminateCheckBox className="text-xl text-secondary/60" />
            )
        return <MdCheckBoxOutlineBlank className="text-xl text-base-content/30" />
    }

    if (!canSave) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 text-center px-6">
                <div className="w-16 h-16 rounded-2xl bg-error/10 flex items-center justify-center">
                    <MdLock className="text-3xl text-error" />
                </div>
                <div>
                    <h1 className="text-xl font-bold text-base-content">
                        Acesso restrito
                    </h1>
                    <p className="text-base-content/60 text-sm mt-1 max-w-md">
                        {isEditing
                            ? 'Você não tem permissão para editar perfis de acesso.'
                            : 'Você não tem permissão para criar perfis de acesso.'}{' '}
                        Fale com o administrador da sua organização.
                    </p>
                </div>
                <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => navigate('/security/permissions')}
                >
                    Voltar aos perfis
                </button>
            </div>
        )
    }

    if (loadingRole || loadingMap) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/security/permissions')}
                    className="btn btn-ghost btn-circle btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-neutral to-neutral/70 flex items-center justify-center shadow-lg">
                        <MdShield className="text-2xl text-white" />
                    </div>
                    <h1 className="text-2xl font-bold text-base-content">
                        {isEditing
                            ? 'Editar Perfil de Acesso'
                            : 'Novo Perfil de Acesso'}
                    </h1>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4">
                        Informações Gerais
                    </h2>

                    <div className="flex flex-col gap-5">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">
                                    Nome do Perfil{' '}
                                    <span className="text-error">*</span>
                                </span>
                            </label>
                            <div className="relative">
                                <MdShield className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                                <input
                                    type="text"
                                    placeholder="Ex: Recepcionista, Enfermeiro(a)..."
                                    className="input input-bordered w-full pl-10"
                                    value={formData.name}
                                    onChange={e =>
                                        setFormData({
                                            ...formData,
                                            name: e.target.value,
                                        })
                                    }
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">
                                    Descrição{' '}
                                    <span className="text-gray-400 text-sm">
                                        (opcional)
                                    </span>
                                </span>
                            </label>
                            <div className="relative">
                                <MdDescription className="absolute left-3 top-3 text-base-content/50 text-xl z-10 pointer-events-none" />
                                <textarea
                                    placeholder="Descreva as responsabilidades deste perfil..."
                                    className="textarea textarea-bordered w-full pl-10 min-h-[80px]"
                                    value={formData.description}
                                    onChange={e =>
                                        setFormData({
                                            ...formData,
                                            description: e.target.value,
                                        })
                                    }
                                />
                            </div>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">
                                    Perfil padrão para (atribuído automaticamente a novos usuários
                                    deste tipo)
                                </span>

                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={formData.defaultForType}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        defaultForType: e.target.value,
                                    })
                                }
                            >
                                <option value="">
                                    Nenhum (não é perfil padrão)
                                </option>
                                <option value="PROFESSIONAL">
                                    Profissional
                                </option>
                                <option value="EMPLOYEE">Funcionário</option>
                                <option value="CLIENT">
                                    Cliente/Paciente
                                </option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4">
                        Permissões
                    </h2>
                    <p className="text-sm text-base-content/60 mb-4">
                        Selecione os módulos e ações que este perfil poderá
                        acessar.
                    </p>

                    {roles.filter(r => String(r.id) !== id).length > 0 && (
                        <div className="flex flex-col sm:flex-row sm:items-end gap-2 mb-6 p-4 bg-base-200/30 rounded-lg border border-base-300/50">
                            <div className="form-control flex-1">
                                <label className="label py-1">
                                    <span className="label-text text-sm">
                                        Copiar permissões de outro perfil
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered select-sm w-full"
                                    value={copyFromId}
                                    onChange={e =>
                                        setCopyFromId(e.target.value)
                                    }
                                >
                                    <option value="">
                                        Selecione um perfil...
                                    </option>
                                    {roles
                                        .filter(r => String(r.id) !== id)
                                        .map(r => (
                                            <option
                                                key={r.id}
                                                value={String(r.id)}
                                            >
                                                {r.name}
                                            </option>
                                        ))}
                                </select>
                            </div>
                            <button
                                type="button"
                                className="btn btn-outline btn-sm gap-2"
                                disabled={!copyFromId}
                                onClick={handleCopyFrom}
                            >
                                <MdContentCopy className="text-base" />
                                Copiar
                            </button>
                        </div>
                    )}

                    <div className="flex flex-col gap-3">
                        {Object.entries(permissionsMap).map(
                            ([moduleKey, moduleData]) => {
                                const isExpanded =
                                    expandedModules[moduleKey] ?? false
                                const moduleState =
                                    getModuleState(moduleKey)
                                const resourceEntries = Object.entries(
                                    moduleData.resources
                                )

                                return (
                                    <div
                                        key={moduleKey}
                                        className="border border-base-300/50 rounded-lg overflow-hidden"
                                    >
                                        <div className="flex items-center gap-3 p-3 bg-base-200/30 hover:bg-base-200/50 transition-colors">
                                            <button
                                                type="button"
                                                className="flex items-center gap-1 cursor-pointer"
                                                onClick={() =>
                                                    toggleAllModule(moduleKey)
                                                }
                                            >
                                                <CheckboxIcon
                                                    state={moduleState}
                                                />
                                            </button>

                                            <button
                                                type="button"
                                                className="flex-1 flex items-center justify-between cursor-pointer"
                                                onClick={() =>
                                                    toggleModule(moduleKey)
                                                }
                                            >
                                                <span className="font-semibold text-sm text-base-content">
                                                    {moduleLabel(
                                                        segment,
                                                        moduleKey,
                                                        moduleData.label
                                                    )}
                                                </span>
                                                <div className="flex items-center gap-2">
                                                    {moduleState !==
                                                        'none' && (
                                                            <span className="badge badge-secondary badge-sm">
                                                                {moduleState ===
                                                                    'all'
                                                                    ? 'Acesso total'
                                                                    : 'Parcial'}
                                                            </span>
                                                        )}
                                                    {isExpanded ? (
                                                        <MdExpandLess className="text-xl text-base-content/50" />
                                                    ) : (
                                                        <MdExpandMore className="text-xl text-base-content/50" />
                                                    )}
                                                </div>
                                            </button>
                                        </div>

                                        {isExpanded && (
                                            <div className="p-3">
                                                <div className="overflow-x-auto">
                                                    <table className="table table-sm w-full">
                                                        <thead>
                                                            <tr>
                                                                <th className="text-xs font-medium text-base-content/50 w-2/5">
                                                                    Recurso
                                                                </th>
                                                                {ACTIONS.map(
                                                                    action => {
                                                                        const colState =
                                                                            getColumnState(
                                                                                moduleKey,
                                                                                action
                                                                            )
                                                                        return (
                                                                            <th
                                                                                key={
                                                                                    action
                                                                                }
                                                                                className="text-center w-[15%]"
                                                                            >
                                                                                <button
                                                                                    type="button"
                                                                                    className="flex flex-col items-center gap-1 w-full cursor-pointer"
                                                                                    onClick={() =>
                                                                                        toggleColumn(
                                                                                            moduleKey,
                                                                                            action
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    <CheckboxIcon
                                                                                        state={
                                                                                            colState
                                                                                        }
                                                                                    />
                                                                                    <span className="text-xs font-medium text-base-content/50">
                                                                                        {
                                                                                            ACTION_LABELS[
                                                                                            action
                                                                                            ]
                                                                                        }
                                                                                    </span>
                                                                                </button>
                                                                            </th>
                                                                        )
                                                                    }
                                                                )}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {resourceEntries.map(
                                                                ([
                                                                    resourceKey,
                                                                    resourceData,
                                                                ]) => {
                                                                    const resourceState =
                                                                        getResourceState(
                                                                            moduleKey,
                                                                            resourceKey
                                                                        )

                                                                    return (
                                                                        <tr
                                                                            key={
                                                                                resourceKey
                                                                            }
                                                                            className="hover:bg-base-200/20"
                                                                        >
                                                                            <td>
                                                                                <button
                                                                                    type="button"
                                                                                    className="flex items-center gap-2 cursor-pointer"
                                                                                    onClick={() =>
                                                                                        toggleAllResource(
                                                                                            moduleKey,
                                                                                            resourceKey
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    <CheckboxIcon
                                                                                        state={
                                                                                            resourceState
                                                                                        }
                                                                                    />
                                                                                    <span className="text-sm text-base-content">
                                                                                        {getResourceLabel(
                                                                                            moduleKey,
                                                                                            resourceKey,
                                                                                            resourceData.label
                                                                                        )}
                                                                                    </span>
                                                                                </button>
                                                                            </td>
                                                                            {ACTIONS.map(
                                                                                action => (
                                                                                    <td
                                                                                        key={
                                                                                            action
                                                                                        }
                                                                                        className="text-center"
                                                                                    >
                                                                                        <label className="flex items-center justify-center cursor-pointer">
                                                                                            <input
                                                                                                type="checkbox"
                                                                                                className="checkbox checkbox-secondary checkbox-sm"
                                                                                                checked={isActionChecked(
                                                                                                    moduleKey,
                                                                                                    resourceKey,
                                                                                                    action
                                                                                                )}
                                                                                                onChange={() =>
                                                                                                    toggleAction(
                                                                                                        moduleKey,
                                                                                                        resourceKey,
                                                                                                        action
                                                                                                    )
                                                                                                }
                                                                                            />
                                                                                        </label>
                                                                                    </td>
                                                                                )
                                                                            )}
                                                                        </tr>
                                                                    )
                                                                }
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )
                            }
                        )}
                    </div>
                </div>


                <div className="flex gap-2 pt-4">
                    <button
                        type="button"
                        className="btn btn-ghost flex-1"
                        onClick={() => navigate('/security/permissions')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary flex-1"
                        disabled={loading || !formData.name.trim()}
                    >
                        {loading ? (
                            <span className="loading loading-spinner"></span>
                        ) : isEditing ? (
                            'Atualizar'
                        ) : (
                            'Salvar'
                        )}
                    </button>
                </div>
            </form>
        </div>
    )
}
